/*
 * DATA_RIGHTS: HONEYWELL CONFIDENTIAL AND PROPRIETARY
 * THIS WORK CONTAINS VALUABLE CONFIDENTIAL AND PROPRIETARY
 * INFORMATION. DISCLOSURE, USE OR REPRODUCTION OUTSIDE OF
 * HONEYWELL INTERNATIONAL, INC. IS PROHIBITED EXCEPT AS
 * AUTHORIZED IN WRITING. THIS UNPUBLISHED WORK IS PROTECTED BY
 * THE LAWS OF THE UNITED STATES AND OTHER COUNTRIES. IN THE
 * EVENT OF PUBLICATION, THE FOLLOWING NOTICE SHALL APPLY:
 * Copyright (C) 2022 HONEYWELL INTERNATIONAL, INC., ALL RIGHTS RESERVED.
 *
 * Created by H144765 on 3/31/2022.
 */

import _ from 'lodash'
import {FIRST_LEVEL_ROUTES, Routes} from '../utils/Routes'
import {LIST_ORDER} from '../utils/Const'
import {showNotification} from "../common/ToastNotification";

const LOCAL_STORAGE_KEY_JWS = 'secret'
const LOCAL_STORAGE_KEY_REFRESH_TOKEN = 'refresh'

const applicationContextPath = () => {
    const pathSegments = window.location.pathname.split('/')
    const pathName = '/' + (pathSegments.length > 1 ? pathSegments[1] : '')
    return pathName.length === 1 || _.indexOf(FIRST_LEVEL_ROUTES, pathName) >= 0 ? '/' : pathName
}

export const APP_CONTEXT_PATH = applicationContextPath()
const INTERNAL_APP_PATH = APP_CONTEXT_PATH !== '/' ? APP_CONTEXT_PATH : ''
const LOGIN_PATH = INTERNAL_APP_PATH + Routes.LOGIN

export const appUrl = (uriString) => {
    return INTERNAL_APP_PATH + standardizeRelativeUri(uriString)
}

export const apiUrl = (uriString) => {
    return INTERNAL_APP_PATH + '/api' + (!uriString ? '' : standardizeRelativeUri(uriString))
}

export const actuatorUrl = (uriString) => {
    return INTERNAL_APP_PATH + '/actuator' + (!uriString ? '' : standardizeRelativeUri(uriString))
}

export const formData = (form) => {
    return Object.keys(form).reduce((acc, field) => {
        const formFieldString = encodeURIComponent(field) + '=' + encodeURIComponent(form[field])
        return !acc ? formFieldString : acc + '&' + formFieldString;
    }, '')
}

export const defaultOptions = () => {
    return {
        credentials: 'same-origin',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json; charset=utf-8',
        },
    }
}

export const securedApiOptions = () => {
    const opts = defaultOptions()
    const csrfToken = getCookie('XSRF-TOKEN');
    const jws = getJwsToken()
    return !jws ? opts : {
        ...opts,
        credentials: 'include',
        headers: {
            ...opts.headers,
            'Authorization': `Bearer ${jws}`,
            'X-XSRF-TOKEN': csrfToken,
        },
    }
}

export const composeCommonQuery = (search, sort, sortOrder) => {
    let query = '?start=0';
    if (search && search.length > 0) {
        query += '&search=' + encodeURIComponent(search)
    }
    if (sort && sort.length > 0) {
        query += '&sort=' + encodeURIComponent(sort)
    }
    query += '&sortOrder='
    let sortOrdering = LIST_ORDER.ASC
    if (sortOrder === -1) {
        sortOrdering = LIST_ORDER.DESC
    }
    query += sortOrdering

    return query
}

const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
};

export const handleApiCall = (url, opts, customSuccessHandler, customErrorHandler) => {
    let currentOpts;
    if (opts) {
        const csrfToken = getCookie('XSRF-TOKEN');
        const defaultOpts = {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': csrfToken,
            },

        };
        currentOpts = {
            ...defaultOpts,
            ...opts,
            headers: {
                ...defaultOpts.headers,
                ...(opts.headers || {}),
            },

        };

    } else {
        currentOpts = {
            ...securedApiOptions(),
            credentials: 'include',
        };

    }
    return fetch(url, currentOpts)
        .then(responseInterceptor(url, customSuccessHandler, customErrorHandler))
        .catch(errorInterceptor(url, customErrorHandler))

}

export const isAuthenticated = () => {
    return getJwsToken()
}

export const saveJwsToken = (secret, refresh = false) => {
    if (!secret || !_.isString(secret)) {
        return
    }
    window.localStorage.setItem(refresh ? LOCAL_STORAGE_KEY_REFRESH_TOKEN : LOCAL_STORAGE_KEY_JWS, JSON.stringify(secret))
}

export const cleanJwsToken = () => {
    window.localStorage.removeItem(LOCAL_STORAGE_KEY_JWS)
}

const getJwsToken = (refresh = false) => {
    const secret = JSON.parse(window.localStorage.getItem(refresh ? LOCAL_STORAGE_KEY_REFRESH_TOKEN : LOCAL_STORAGE_KEY_JWS))
    return !secret || !_.isString(secret) ? '' : secret
}

export default {
    defaultOptions, securedApiOptions, handleApiCall,
    isAuthenticated, cleanJwsToken, saveJwsToken,
}

export const standardizeRelativeUri = (url) => {
    return _.startsWith(url, '/') ? url : `/${url}`
}

const responseInterceptor = (originalApiUrl, customHandlerCallback, customErrorHandler) => {
    return (response) => {
        if (response.status === 401 && !isAuthenticated() && originalApiUrl.indexOf(apiUrl(Routes.TOKEN)) === -1) {
            //URL check is for when trying to login with wrong credentials
            cleanJwsToken()
            redirectLogin()
        }
        if (response.status === 401 && isAuthenticated() && originalApiUrl.indexOf(apiUrl(Routes.TOKEN)) === -1) {
            //only try to refresh token when request was not for token refresh
            return refreshTokenOrRedirect().then(() => {
                return handleApiCall(originalApiUrl, securedApiOptions(), customHandlerCallback, customErrorHandler)
            })
        } else if (customHandlerCallback) {
            const callbackResult = customHandlerCallback(response)
            if (_.isBoolean(callbackResult) && callbackResult) {
                return response.json()
            } else if (callbackResult instanceof Promise) {
                return callbackResult
            } else {
                throw new Error("Unable to process result of custom callback in HTTP response interceptor")
            }
        } else if (response.status === 204) {
            return
        } else if (response.ok) {
            if (response.headers.get("content-type").indexOf('application/json') === 0) {
                return response.json()
            } else {
                return response
            }
        } else {
            const errorMessage = 'Unexpected response for API call'
            if (_.isString(originalApiUrl)) {
                console.log(`${errorMessage} ${originalApiUrl}, returned status: ${response.status}`, response)
            } else {
                console.log(`${errorMessage}, returned status: ${response.status}`, response)
            }
            if (customErrorHandler) {
                return customErrorHandler(response)
            } else {
                return response.json().catch(() => {
                    throw { generalError: true };
                }).then(err => {
                if (response.status === 400 && err.errorKey === "INVALID_INPUT") {
                    showNotification({
                        translationPropertyKey: "INVALID_INPUT", // or custom message
                        severity: "critical",
                    });
                } else if (response.status >= 500 && originalApiUrl !== '/hdr/api/tokens') {
                    showNotification({
                        translationPropertyKey: "GENERAL_ERROR",
                        severity: "critical",
                    });
                }
                    throw {
                        generalError: response.status >= 500,
                        ...err,
                    }
                })
            }
        }
    }
}

export const isLoginPage = () => {
    return window.location.pathname === LOGIN_PATH
}

const redirectLogin = () => {
    window.location = LOGIN_PATH;
}

const errorInterceptor = (url, customErrorHandler) => {
    return (error) => {
        const errorMessage = 'Application API call failed'
        if (customErrorHandler) {
            return customErrorHandler(error)
        } else if (_.isString(url)) {
            console.log(`${errorMessage} failed for ${url}`, error)
        } else {
            console.log(errorMessage, error)
        }
        throw error
    }
}

let refreshTokenRequestPromise = null

export const refreshTokenOrRedirect = () => {
    const refreshToken = getJwsToken(true);
    if (refreshToken) {
        //if refresh token request is ongoing, do not issue a new request, rather return the existing one
        if (refreshTokenRequestPromise != null) {
            return refreshTokenRequestPromise
        } else {
            //store refresh token request promise
            refreshTokenRequestPromise = signInToken(refreshToken).then(() => {
                refreshTokenRequestPromise = null;
                if (!isAuthenticated()) {
                    redirectLogin()
                    throw new Error()
                }
            }).catch(() => {
                // mark that we're done refreshing the token, so we can start a new request if needed
                refreshTokenRequestPromise = null;
            })
            return refreshTokenRequestPromise
        }
    } else {
        redirectLogin()
    }
}

const signInToken = (token) => {
    const defaultOpts = defaultOptions()
    const opts = {
        ...defaultOpts,
        method: 'POST',
        headers: {
            ...defaultOpts.headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            refreshToken: token,
        }),
    }

    return handleApiCall(apiUrl(Routes.TOKEN), opts)
        .then(secret => {
            if (!secret) {
                return
            }
            saveJwsToken(secret.authToken)
            saveJwsToken(secret.refreshToken, true)
        }).catch(() => {
            cleanJwsToken()
            redirectLogin()
        })
}

export const getEnumParams = (paramName, values) => {
    let param = '';
    if (values) {
        values.forEach(s => {
            param += '&' + paramName + '=' + s
        })
    }
    return param
}